import type {
  BlueprintCalibration,
  BlueprintUnderlay,
} from '@shared/models/design/blueprint-underlay.model';
import type { Point2D } from '@shared/models/geometry.model';
import { getDistance } from '../geometry/coordinates.helpers';
import { isBlueprintUnderlaySiblingOf } from './blueprint-underlay-building.helpers';
import {
  alignBlueprintUnderlayPose,
  blueprintPageUVToMetric,
  calibrateBlueprintByKnownLength,
  getBlueprintDrawingScaleRatio,
  metricPointToBlueprintPageUV,
  setBlueprintDrawingScaleRatio,
} from './blueprint-underlay.helpers';

/** Whether a measured/inherited calibration should apply to one page or every page. */
export type BlueprintCalibrationApplyScope = 'active' | 'all';

/**
 * @deprecated Prefer {@link isDrawingScaleCloseToMeasured} for import scale checks.
 */
export type CalibrationVerificationBand = 'good' | 'warning' | 'bad';

const GOOD_MAX_RATIO = 0.005; // < 0.5%
/** Relative |entered − measured| / measured above this ⇒ scale mismatch warning. */
export const DRAWING_SCALE_SIGNIFICANT_DIFFERENCE_RATIO = 0.03; // 3%

/**
 * @deprecated Legacy verification helper.
 * Map relative error to traffic-light status.
 * Green &lt; 0.5%, yellow 0.5–3%, red &gt; 3%.
 */
export const getCalibrationVerificationBand = (
  differenceRatio: number,
): CalibrationVerificationBand => {
  if (!(differenceRatio >= 0) || !Number.isFinite(differenceRatio)) {
    return 'bad';
  }
  if (differenceRatio < GOOD_MAX_RATIO) return 'good';
  if (differenceRatio <= DRAWING_SCALE_SIGNIFICANT_DIFFERENCE_RATIO) {
    return 'warning';
  }
  return 'bad';
};

/**
 * Absolute relative error between measured and expected lengths.
 */
export const getCalibrationDifferenceRatio = (
  measuredMeters: number,
  expectedMeters: number,
): number | undefined => {
  if (!(expectedMeters > 0) || !Number.isFinite(expectedMeters)) {
    return undefined;
  }
  if (!(measuredMeters >= 0) || !Number.isFinite(measuredMeters)) {
    return undefined;
  }
  return Math.abs(measuredMeters - expectedMeters) / expectedMeters;
};

/**
 * Relative difference between a measured `1:N` and an entered `1:N`.
 */
export const getDrawingScaleDifferenceRatio = (
  measuredRatio: number,
  enteredRatio: number,
): number | undefined => {
  if (!(measuredRatio > 0) || !Number.isFinite(measuredRatio)) return undefined;
  if (!(enteredRatio > 0) || !Number.isFinite(enteredRatio)) return undefined;
  return Math.abs(enteredRatio - measuredRatio) / measuredRatio;
};

/**
 * True when entered drawing scale is within
 * {@link DRAWING_SCALE_SIGNIFICANT_DIFFERENCE_RATIO} of the measured scale.
 */
export const isDrawingScaleCloseToMeasured = (
  measuredRatio: number,
  enteredRatio: number,
): boolean => {
  const diff = getDrawingScaleDifferenceRatio(measuredRatio, enteredRatio);
  if (diff === undefined) return false;
  return diff <= DRAWING_SCALE_SIGNIFICANT_DIFFERENCE_RATIO;
};

/**
 * Scale the underlay from a known span and persist primary calibration in UV.
 * Replaces any previous {@link BlueprintUnderlay.calibration} evidence (primary + verification).
 */
export const applyBlueprintPrimaryCalibration = (
  underlay: BlueprintUnderlay,
  start: Point2D,
  end: Point2D,
  knownLengthMeters: number,
): BlueprintUnderlay | undefined => {
  if (!(knownLengthMeters > 0) || !Number.isFinite(knownLengthMeters)) {
    return undefined;
  }
  if (!(getDistance(start, end) > 0)) return undefined;

  // UV is scale-invariant — capture against the pre-scale frame.
  const primary: BlueprintCalibration['primary'] = {
    start: metricPointToBlueprintPageUV(underlay, start),
    end: metricPointToBlueprintPageUV(underlay, end),
    knownLengthMeters,
  };

  const scaled = calibrateBlueprintByKnownLength(
    underlay,
    start,
    end,
    knownLengthMeters,
  );

  return {
    ...scaled,
    drawingScaleSource: 'measure',
    // Always a single primary marking — drop any previous verification span.
    calibration: { primary },
  };
};

/**
 * Prefer a page that was measured (not inherited), else any calibrated page.
 */
export const findBlueprintCalibrationSource = (
  underlays: readonly BlueprintUnderlay[],
): BlueprintUnderlay | undefined =>
  underlays.find(
    (underlay) =>
      underlay.calibration?.primary && !underlay.calibration.inherited,
  ) ?? underlays.find((underlay) => underlay.calibration?.primary);

/**
 * Copy drawing scale + calibrated status from {@link source} onto {@link underlay}.
 * Also copies map pose so a new page lands on the same sheet frame.
 * Does not copy measurable UV markings (flagged {@link BlueprintCalibration.inherited}).
 */
export const inheritBlueprintCalibrationFromSource = (
  underlay: BlueprintUnderlay,
  source: BlueprintUnderlay,
): BlueprintUnderlay => {
  const primary = source.calibration?.primary;
  if (!primary) return underlay;
  const ratio = getBlueprintDrawingScaleRatio(source);
  const scaled =
    ratio !== undefined
      ? setBlueprintDrawingScaleRatio(underlay, ratio)
      : underlay;
  return {
    ...alignBlueprintUnderlayPose(scaled, source),
    drawingScaleSource: 'measure',
    calibration: {
      primary: {
        start: primary.start,
        end: primary.end,
        knownLengthMeters: primary.knownLengthMeters,
      },
      inherited: true,
    },
  };
};

/**
 * After measuring {@link calibratedActive}, optionally propagate its `1:N` scale
 * and calibrated status to every other page of the same building.
 */
export const applyBlueprintCalibrationToUnderlays = (
  underlays: readonly BlueprintUnderlay[],
  calibratedActive: BlueprintUnderlay,
  scope: BlueprintCalibrationApplyScope,
  /** Building in view; scopes "all" when the active page has none. */
  buildingId: string | undefined,
): BlueprintUnderlay[] => {
  if (scope === 'active') {
    return underlays.map((underlay) =>
      underlay.id === calibratedActive.id ? calibratedActive : underlay,
    );
  }

  const primary = calibratedActive.calibration?.primary;
  const ratio = getBlueprintDrawingScaleRatio(calibratedActive);
  if (!primary || ratio === undefined) {
    return underlays.map((underlay) =>
      underlay.id === calibratedActive.id ? calibratedActive : underlay,
    );
  }

  return underlays.map((underlay) => {
    if (underlay.id === calibratedActive.id) return calibratedActive;
    // Another building's pages keep their own scale.
    if (!isBlueprintUnderlaySiblingOf(underlay, calibratedActive, buildingId)) {
      return underlay;
    }
    // Match scale and pose so sibling pages stack on the same map frame.
    return {
      ...alignBlueprintUnderlayPose(
        setBlueprintDrawingScaleRatio(underlay, ratio),
        calibratedActive,
      ),
      drawingScaleSource: 'measure',
      calibration: {
        primary: {
          start: primary.start,
          end: primary.end,
          knownLengthMeters: primary.knownLengthMeters,
        },
        inherited: true,
      },
    };
  });
};

/**
 * True when any underlay already has primary calibration evidence.
 */
export const hasBlueprintCalibration = (
  underlays: readonly BlueprintUnderlay[],
): boolean => underlays.some((underlay) => underlay.calibration?.primary);

/**
 * Remove persisted calibration markings without changing the current scale.
 */
export const clearBlueprintCalibration = (
  underlay: BlueprintUnderlay,
): BlueprintUnderlay => {
  if (!underlay.calibration) return underlay;
  const { calibration: _removed, ...rest } = underlay;
  return rest;
};

/**
 * @deprecated Legacy verification write path. Unused by the current UI.
 * Record a verification span against the current calibrated scale.
 */
export const applyBlueprintVerificationCalibration = (
  underlay: BlueprintUnderlay,
  start: Point2D,
  end: Point2D,
  expectedLengthMeters: number,
): BlueprintUnderlay | undefined => {
  const primary = underlay.calibration?.primary;
  if (!primary) return undefined;
  if (!(expectedLengthMeters > 0) || !Number.isFinite(expectedLengthMeters)) {
    return undefined;
  }

  const measuredLengthMeters = getDistance(start, end);
  if (!(measuredLengthMeters > 0)) return undefined;

  const differenceRatio = getCalibrationDifferenceRatio(
    measuredLengthMeters,
    expectedLengthMeters,
  );
  if (differenceRatio === undefined) return undefined;

  return {
    ...underlay,
    calibration: {
      primary,
      verification: {
        start: metricPointToBlueprintPageUV(underlay, start),
        end: metricPointToBlueprintPageUV(underlay, end),
        expectedLengthMeters,
        measuredLengthMeters,
        differenceRatio,
      },
    },
  };
};

/**
 * Resolve a stored primary/verification span to current local-meter endpoints.
 */
export const resolveCalibrationSpanMeters = (
  underlay: BlueprintUnderlay,
  span: { start: { u: number; v: number }; end: { u: number; v: number } },
): { start: Point2D; end: Point2D; lengthMeters: number } => {
  const start = blueprintPageUVToMetric(underlay, span.start);
  const end = blueprintPageUVToMetric(underlay, span.end);
  return {
    start,
    end,
    lengthMeters: getDistance(start, end),
  };
};

/**
 * Format meters for calibration status copy (one decimal).
 */
export const formatCalibrationMeters = (meters: number): string => {
  if (!Number.isFinite(meters)) return '—';
  return meters.toFixed(1);
};

/**
 * Format a difference ratio as a percentage string (e.g. `0.2%`).
 */
export const formatCalibrationDifferencePercent = (
  differenceRatio: number,
): string => {
  if (!Number.isFinite(differenceRatio)) return '—';
  const percent = differenceRatio * 100;
  if (percent < 0.1) return `${percent.toFixed(2)}%`;
  if (percent < 10) return `${percent.toFixed(1)}%`;
  return `${Math.round(percent)}%`;
};
